// Libraries
import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// @mui material components
import Grid from "@mui/material/Grid2";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { scrollToTop, dateFormatWithDash, useHandleError } from "components/helper";
// Redux Actions

import { addVoucher, clearErrors, setLoading } from "store/actions/voucherActions";
import setAlert from "store/actions/alertActions";

// Layouts
import DashboardLayout from "layouts/DashboardLayout";

// Custom Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";

import AdditionalInfo from "./components/AdditionalInfo";
import Review from "./components/Review";
import Information from "./components/Information";

// Schema

import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";

function getSteps() {
  return ["Information", "Additional Info", "Review"];
}

function getStepContent(
  stepIndex,
  formData,
  handleRecommendationChange,
  selectedRecommendation,
  handleLocationChange,
  selectedLocation
) {
  switch (stepIndex) {
    case 0:
      return (
        <Information
          formData={formData}
          selectedLocation={selectedLocation}
          selectedRecommendation={selectedRecommendation}
          handleLocationChange={handleLocationChange}
          handleRecommendationChange={handleRecommendationChange}
        />
      );
    case 1:
      return <AdditionalInfo formData={formData} />;
    case 2:
      return <Review formData={formData} />;
    default:
      return null;
  }
}

const NewVoucher = () => {
  //   const formRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // selector
  const error = useSelector((state) => state.recommendations.error);
  const user = useSelector((state) => state.users.user);

  // state
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(
    user?.role === "partner" && user?.partnerPower ? user?.locations[0] : null
  );
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const { formId, formField } = form;
  const currentValidation = validations[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  //   const [formRefVal, setFormRefVal] = useState(null);
  const handleNavigation = () => {
    let url = null;
    if (user.role === "partner" && user?.partnerPower) {
      url = `/partnerPower/vouchers?locationId=${selectedLocation?._id}`;
      navigate(url);
    }
    if (user.role === "admin") {
      url = `/admin/vouchers?`;
      navigate(url);
    } else if (user.role === "partner" && !user?.partnerPower) {
      url = `/partner/vouchers?locationId=${Location}`;
      navigate(url);
    }
  };
  const handleRecommendationChange = (value) => {
    if (value && value?._id) {
      setSelectedRecommendation(value);
    } else {
      setSelectedRecommendation({});
    }
  };

  const handleLocationChange = (value) => {
    if (value && value?._id) {
      setSelectedLocation(value);
    } else {
      setSelectedLocation({});
    }
  };
  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    scrollToTop(); // Scroll to the top
  };

  const submitForm = async (values, actions) => {
    await sleep(1000);
    const updatedValues = { ...values };

    if (updatedValues && updatedValues?.logo) {
      delete updatedValues?.logo;
    }
    if (updatedValues && updatedValues?.location && updatedValues?.location?._id) {
      updatedValues.location = updatedValues?.location?._id;
    }
    if (updatedValues && updatedValues?.dayRestriction) {
      if (updatedValues?.dayRestriction === "per_day") {
        updatedValues.dayRestriction = true;
      } else updatedValues.dayRestriction = false;
    }

    dispatch(setLoading());
    // eslint-disable-next-line no-alert
    dispatch(addVoucher(updatedValues, values?.logo, user?.role));
    actions.setSubmitting(false);
    actions.resetForm();

    setActiveStep(0);
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
      handleNavigation();
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
      scrollToTop(); // Scroll to the top
    }
  };

  const handleStartDate = (newValue) => {
    setStartDate(dateFormatWithDash(newValue[0]));
  };

  const handleEndDate = (newValue) => {
    setEndDate(dateFormatWithDash(newValue[0]));
  };

  // useEffect
  useEffect(() => {
    setSteps(getSteps("admin"));
  }, []);

  useHandleError(error, dispatch, setAlert, clearErrors); // Use the custom hook
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} mb={20}>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Formik
              initialValues={initialValues}
              validationSchema={steps[activeStep] === "Review" ? undefined : currentValidation}
              onSubmit={handleSubmit}
              //   innerRef={(f) => {
              //     setFormRefVal(f?.values);
              //     formRef.current = f;
              //   }}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <MDBox mx={2} mt={-3}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </MDBox>
                    <MDBox p={3}>
                      <MDBox>
                        {getStepContent(
                          activeStep,
                          {
                            values,
                            touched,
                            formField,
                            errors,
                            setFieldValue,
                          },
                          handleRecommendationChange,
                          selectedRecommendation,
                          handleLocationChange,
                          selectedLocation,
                          handleStartDate,
                          handleEndDate,
                          startDate,
                          endDate,
                          setStartDate,
                          setEndDate
                        )}
                        <MDBox mt={2} width="100%" display="flex" justifyContent="space-between">
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton variant="gradient" color="light" onClick={handleBack}>
                              back
                            </MDButton>
                          )}
                          <div>
                            <MDButton
                              onClick={() => handleNavigation()}
                              rel="noreferrer"
                              variant="outlined"
                              color="dark"
                              sx={{ marginRight: "8px" }}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              disabled={isSubmitting}
                              type="submit"
                              variant="gradient"
                              color="dark"
                            >
                              {isLastStep ? "add" : "next"}
                            </MDButton>
                          </div>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewVoucher;
